import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  addWorkShift,
  getAllUserProfiles,
  getJobPositions,
  getUnit,
  getWorkShiftById,
  updateWorkShift,
} from "../../../Api/services/org-StructureService";
import { getjobpostionsOptions } from "../../../Api/services/user-Management";
import {
  customStylesDropdown,
  mapDropdownOptions,
} from "../../../utils/common/commonFunction";
import { handleKeyDown } from "../../../utils/common/inputValidation";
import CustomInput from "../../../components/CustomeInput";
import MyTable from "../../../components/CustomTable";
import Loader from "../../../components/Loader";
import delete_employee from "../../../images/delete_employee.svg";
import { WorkShiftSchema } from "../../../utils/validators/workShiftValidation";
import { useNavigate, useParams } from "react-router-dom";
import { withNamespaces } from "react-i18next";

function AddWorkShift({ t }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const schema = WorkShiftSchema(t);
  const {
    loading,
    unitOptions,
    jobPositionOptions,
    userProfilesData,
    singleWorkShiftData,
  } = useSelector((state) => state?.orgStructure);

  // Dropdown states
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedJobPosition, setSelectedJobPosition] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Data for employee selection
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsersFinal, setSelectedUsersFinal] = useState([]);
  // const [selectedUsersFinalError, setSelectedUsersFinalError] = useState(false);

  // Initialize form validation
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Map dropdown options
  const unitDropdownOptions = mapDropdownOptions(
    unitOptions,
    "name",
    undefined,
    true
  );

  const filteredJobPositionsByUnit = selectedUnit?.value
    ? jobPositionOptions?.filter(
        (position) => position.unit_id === selectedUnit?.value
      )
    : jobPositionOptions;

  const jobPositionsDropdown = mapDropdownOptions(
    filteredJobPositionsByUnit,
    "name",
    undefined,
    true
  );

  // Conditionally filter user profiles based on selectedUnit and selectedJobPosition values
  const filteredUserProfiles =
    userProfilesData?.data?.filter((user) => {
      const unitMatch = selectedUnit?.value
        ? user.unit_id === selectedUnit.value
        : true;
      const jobPositionMatch = selectedJobPosition?.value
        ? user.job_position_id === selectedJobPosition.value
        : true;

      return unitMatch && jobPositionMatch;
    }) || [];

  // Map filtered user profiles to dropdown options
  const mappedUserProfiles = filteredUserProfiles.map((user) => ({
    option: `${user.first_name} ${user.last_name}`,
    id: user.id,
  }));

  // Convert mapped user profiles to dropdown options
  const userProfilesDropdown = mapDropdownOptions(
    mappedUserProfiles,
    undefined,
    undefined,
    true
  );

  // Watch selectedUnit, selectedJobPosition, and selectedEmployee changes to update filteredUsers
  useEffect(() => {
    // After adding employees to selectedUsersFinal, reset filters and empty filteredUsers
    if (!selectedUnit && !selectedJobPosition && !selectedEmployee) {
      setFilteredUsers([]);
      return;
    }

    const filtered =
      userProfilesData?.data?.filter((user) => {
        // Apply only selected filters
        const unitMatch = selectedUnit?.value
          ? user.unit_id === selectedUnit.value
          : true;
        const jobPositionMatch = selectedJobPosition?.value
          ? user.job_position_id === selectedJobPosition.value
          : true;
        const employeeMatch = selectedEmployee?.value
          ? user.id === selectedEmployee.value
          : true;
        // Return users based on selected filters
        return unitMatch && jobPositionMatch && employeeMatch;
      }) || [];

    // Exclude users already in selectedUsersFinal
    const uniqueFiltered = filtered.filter(
      (user) => !selectedUsersFinal.some((selected) => selected.id === user.id)
    );

    setFilteredUsers(uniqueFiltered);
  }, [
    selectedUnit,
    selectedJobPosition,
    selectedUsersFinal,
    userProfilesData,
    selectedEmployee,
  ]);

  useEffect(() => {
    if (!id) return;
    dispatch(getWorkShiftById(id));
  }, [id]);

  // Fetch dropdown options data
  const fetchDropdownData = async () => {
    await dispatch(getjobpostionsOptions());
    await dispatch(getJobPositions());
    await dispatch(getUnit());
    await dispatch(
      getAllUserProfiles({ order_by: "full_name", order_type: "asc" })
    );
  };

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Pre-fill data if in edit mode
  useEffect(() => {
    if (id && singleWorkShiftData && singleWorkShiftData.id === id) {
      setValue("title", singleWorkShiftData.title);
      setValue("monday", singleWorkShiftData.monday);
      setValue("tuesday", singleWorkShiftData.tuesday);
      setValue("wednesday", singleWorkShiftData.wednesday);
      setValue("thursday", singleWorkShiftData.thursday);
      setValue("friday", singleWorkShiftData.friday);
      setValue("saturday", singleWorkShiftData.saturday);
      setValue("sunday", singleWorkShiftData.sunday);
      setValue("start_time", singleWorkShiftData.start_time);
      setValue("end_time", singleWorkShiftData.end_time);
      setValue("is_active", singleWorkShiftData.is_active);
    }
  }, [singleWorkShiftData, setValue]);

  // Select mon-fri checkboxes by default when creating new shift
  useEffect(() => {
    if (!id) {
      setValue("monday", true);
      setValue("tuesday", true);
      setValue("wednesday", true);
      setValue("thursday", true);
      setValue("friday", true);
    }
  }, [id, setValue]);

  // on edit mode, set selected users
  useEffect(() => {
    if (
      id &&
      !!singleWorkShiftData?.employees?.length &&
      !!userProfilesData?.data?.length
    ) {
      setSelectedUsersFinal(
        singleWorkShiftData?.employees.map((employee) =>
          userProfilesData?.data.find((user) => user.id === employee.id)
        )
      );
    }
  }, [singleWorkShiftData, userProfilesData]);

  // useEffect(() => {
  //   if (selectedUsersFinal.length) setSelectedUsersFinalError(false);
  // }, [selectedUsersFinal]);

  // Form submit
  const onSubmit = async (data) => {
    {
      /*TODO check if users are mandatory when creating work shift*/
    }
    // if (!selectedUsersFinal.length) {
    //   setSelectedUsersFinalError(true);
    //   return;
    // }

    const formattedData = {
      ...data,
      employees: selectedUsersFinal?.map((employee) => employee.id),
    };

    console.log(formattedData);

    if (id) {
      const result = await dispatch(
        updateWorkShift({ id, data: formattedData })
      );
      if (result?.payload && result.payload.success === true) {
        navigate("/admin/work-shifts");
      }
      return;
    }

    const result = await dispatch(addWorkShift(formattedData));

    if (result?.payload && result.payload.success === true) {
      navigate("/admin/work-shifts");
    }
  };

  // Remove employee from list
  const removeEmployeeFromList = (employeeId, listType) => {
    if (listType === "selectedUsersFinal") {
      setSelectedUsersFinal((prev) =>
        prev.filter((employee) => employee.id !== employeeId)
      );
    }
    if (listType === "filteredUsers") {
      setFilteredUsers((prev) =>
        prev.filter((employee) => employee.id !== employeeId)
      );
    }
  };

  // Render employee rows
  const employeeRow = (employee) => {
    return (
      <div className="flex flex-row flex-nowrap border-b-2">
        <span className=" text-gray_900 text-sm font-medium ml-[10px]">
          {`${employee?.first_name} ${employee?.last_name}`},
        </span>
        <span className="text-gray_900 text-sm font-light ml-[10px]">{`${t(
          "employees.basic_information.job_position"
        )}: ${employee?.job_position_title},`}</span>
        <span className="text-gray_900 text-sm font-light ml-[10px]">{`${t(
          "organizations.job_postions_lables.organizationalUnit"
        )}: ${employee?.unit_title}`}</span>
        <img
          src={delete_employee}
          alt="delete"
          className="w-5 h-5 ml-auto cursor-pointer"
          onClick={() => removeEmployeeFromList(employee?.id, "filteredUsers")}
        />
      </div>
    );
  };

  // Define columns for MyTable component
  const columns = React.useMemo(
    () => [
      {
        Header: t("users.full_name"),
        accessor: (row) =>
          `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
        key: "full_name",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: t("users.job_position"),
        accessor: (row) => row.job_position_title,
        key: "job_position_title",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: t("users.orgUnit"),
        accessor: (row) => row.unit_title || "-",
        key: "unit_title",
      },
      {
        Header: t("users.email"),
        key: "email",
        accessor: (row) => row?.user?.email || "-",
      },
      {
        Header: t("users.phone_number"),
        accessor: "phone",
        key: "phone",
        Cell: ({ row: { original } }) => {
          const { phone, phone_country_code } = original.user;
          const phoneNumber = phone
            ? `${phone_country_code || ""} ${phone}`.trim()
            : "-";
          return <span>{phoneNumber}</span>;
        },
      },
      {
        Header: " ",
        key: "",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="w-20 text-end">
            <div className="xl:absolute  xl:right-7 flex items-center justify-end">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  removeEmployeeFromList(
                    row?.original?.id,
                    "selectedUsersFinal"
                  );
                }}
                className=" mr-1"
              >
                <img
                  src={delete_employee}
                  alt="delete"
                  className="w-5 h-5 ml-auto"
                />
              </button>
            </div>
          </div>
        ),
      },
    ],
    [userProfilesData]
  );

  // Add all filtered users to selected users list
  const chooseEmployees = () => {
    setSelectedUsersFinal((prev) => [...prev, ...filteredUsers]);
    setSelectedUnit(null);
    setSelectedEmployee(null);
    setSelectedJobPosition(null);
  };

  const CheckboxContainer = ({ name, label, register }) => (
    <div className="items-center flex gap-2 text-base cursor-pointer">
      <input type="checkbox" {...register(name)} />
      <label htmlFor="checkbox" className="text-gray_900 text-base font-thin">
        {label}
      </label>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="border-2 border-white rounded-[14px] px-[22px] py-5 bg-gradient-to-r from-[#ffffff5e] to-[#ffffffcc] my-6 2xl:mr-5 mr-3">
        <h6 className=" mb-7 text-gray-900 font-semibold">
          {t("work_shifts.work_shift_settings")}
        </h6>

        {/* Dropdown Filters */}
        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
          <div className="grid md:grid-cols-5 grid-cols-1 gap-4 h-max">
            <div className="col-span-3">
              <CustomInput
                type="text"
                name="title"
                label={t("leave.form.title")}
                id="title"
                onKeyDown={handleKeyDown}
                placeholder={t("leave.form.title")}
                register={{ ...register("title") }}
                error={errors?.title}
              />
              <div className="grid grid-cols-2 gap-2 mt-5 mb-2">
                <CustomInput
                  type="time"
                  name="start_time"
                  label={t("work_shifts.start_time")}
                  id="start_time"
                  placeholder={t("work_shifts.start_time")}
                  register={{ ...register("start_time") }}
                  error={errors?.start_time}
                />
                <CustomInput
                  type="time"
                  name="end_time"
                  label={t("work_shifts.end_time")}
                  id="end_time"
                  placeholder={t("work_shifts.end_time")}
                  register={{ ...register("end_time") }}
                  error={errors?.end_time}
                />
              </div>
              <div className="grid grid-cols-5 col-span-3 gap-2 p-2">
                <CheckboxContainer
                  label={t("work_shifts.table.mon")}
                  name="monday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.tue")}
                  name="tuesday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.wed")}
                  name="wednesday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.thu")}
                  name="thursday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.fri")}
                  name="friday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.sat")}
                  name="saturday"
                  register={register}
                />
                <CheckboxContainer
                  label={t("work_shifts.table.sun")}
                  name="sunday"
                  register={register}
                />
                <div className="col-span-5">
                  <CheckboxContainer
                    label={t("work_shifts.active")}
                    name="is_active"
                    register={register}
                  />
                </div>
              </div>
            </div>
            <div className="grid col-span-2 gap-4 md:mt-0 sm:mt-4 max-h-36">
              <div className=" rounded-[10px] w-full col-span-2">
                <Select
                  options={unitDropdownOptions}
                  placeholder={t("leave.form.unitPlaceholder")}
                  onChange={(selectedOption) => {
                    setSelectedUnit(selectedOption);
                    setSelectedJobPosition(null);
                    setSelectedEmployee(null);
                  }}
                  value={selectedUnit}
                  styles={{
                    ...customStylesDropdown,
                    control: (provided) => ({
                      ...provided,
                      width: "100%",
                      borderRadius: "10px",
                    }),
                  }}
                />
              </div>
              <div className=" rounded-[10px] w-full col-span-2">
                <Select
                  options={jobPositionsDropdown}
                  placeholder={t("users.job_position")}
                  onChange={(selectedOption) => {
                    setSelectedJobPosition(selectedOption);
                    setSelectedEmployee(null);
                  }}
                  value={selectedJobPosition}
                  styles={{
                    ...customStylesDropdown,
                    control: (provided) => ({
                      ...provided,
                      minWidth: 100,
                      borderRadius: 10,
                    }),
                  }}
                />
              </div>
              <div className=" rounded-[10px] w-full col-span-2 md:mb-0 sm:mb-4">
                <Select
                  onChange={(selectedOption) =>
                    setSelectedEmployee(selectedOption)
                  }
                  value={selectedEmployee}
                  options={userProfilesDropdown}
                  placeholder={t("headers_breadcrum.employee")}
                  styles={{
                    ...customStylesDropdown,
                    control: (provided) => ({
                      ...provided,
                      width: "100%",
                      borderRadius: "10px",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Employees List */}
          <div className="flex flex-col gap-2 ">
            <div className="flex flex-col gap-2 overflow-y-auto max-h-[250px] mb-2">
              {filteredUsers?.map((employee) => employeeRow(employee))}
            </div>
            <div className="flex justify-end mt-auto">
              <button
                onClick={chooseEmployees}
                type="button"
                className="mr-1 border border-primary_orange text-sm font-semibold font-pop text-primary_orange py-2 px-[26px] rounded-[14px] transition-all ease-in-out duration-300 hover:bg-primary_orange hover:text-white bg-transparent"
              >
                {t("employees.choose_employees")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Final Selected Employees Container */}
      <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto mt-4">
        <MyTable
          columns={columns}
          data={selectedUsersFinal}
          hidePagination={true}
          loading={loading}
          tableTitle={t("employees.chosen_employees")}
        />
      </div>

      {/*TODO check if users are mandatory when creating work shift*/}
      {/*{selectedUsersFinalError && (*/}
      {/*  <p className="text-red-500 text-xs pt-2">*/}
      {/*    {t('leave.validation.employees')}*/}
      {/*  </p>*/}
      {/*)}*/}

      {/* Form Submission Buttons */}
      <div className="flex justify-end bg-gray_50 rounded-[14px] py-5 px-[10px]">
        <button
          // onClick={onClose}
          type="button"
          className="mr-1 border border-primary_orange text-sm font-semibold font-pop text-primary_orange py-2 px-[26px] rounded-[14px] transition-all ease-in-out duration-300 hover:bg-primary_orange hover:text-white bg-transparent"
        >
          {t("leave.form.cancelButtonText")}
        </button>
        <button
          disabled={loading}
          type="submit"
          className="mr-4 text-sm font-semibold font-pop text-white border border-transparent hover:border-primary_orange hover:text-primary_orange hover:bg-transparent py-2 px-[26px] rounded-[14px] transition-all ease-in-out duration-300 bg-primary_orange"
        >
          {loading ? (
            <Loader />
          ) : id ? (
            t("leave.form.updateButtonText")
          ) : (
            t("leave.form.saveButtonText")
          )}
        </button>
      </div>
    </form>
  );
}

export default withNamespaces()(AddWorkShift);
